import React, { useRef, useEffect, useState } from 'react';
import useNonNullContext from 'src/hooks/useNonNullContext';
import ConfigContext from 'src/context/config';

import { Grid, Link, Typography, Alert } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useRouter } from 'next/router';

import Form from './Form';
import SocialSignupAndLogin from './SocialSignupAndLogin';
import ReCAPTCHA from 'react-google-recaptcha';

const SignupAndLogin: React.FC<Props> = ({
  state,
  defaultSignupValues,
  onSuccess,
  referralCode,
  title,
  minHeight,
}) => {
  //it's either login or signup
  const [isSignup, setIsSignup] = React.useState(state === 'signup');

  const router = useRouter();
  const { query } = router;

  const theme = useTheme();
  const isAuthPage =
    router.pathname === '/signup' || router.pathname === '/login';

  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const configContext = useNonNullContext(ConfigContext);

  const [hasSignupError, setHasSignupError] = useState(false);

  useEffect(() => {
    if (!recaptchaRef.current) {
      return;
    }

    recaptchaRef.current.executeAsync().then((token) => {
      setRecaptchaToken(token);
    });
  }, []);

  return (
    <Grid
      container
      sx={{
        flexDirection: 'column',
        alignItems: 'center',
        minHeight: minHeight || '70vh',
        paddingBottom: '2.5rem',
      }}
    >
      {hasSignupError && (
        <Alert severity={'error'}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
            Oops! It appears our system has flagged your request, preventing us
            from creating your account.
          </Typography>
          <Typography variant="subtitle1">
            Please refresh the page and try again, or contact support if the
            issue persists.
          </Typography>
        </Alert>
      )}
      <ReCAPTCHA
        sitekey={
          // This is in here for dev/stg since all of the recaptcha keys on tenants are production keys. Allows us to override to staging key.
          process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ||
          configContext.clientSideKeys.recaptchaSiteKey
        }
        size="invisible"
        onChange={() => {}}
        ref={recaptchaRef}
      />
      {query?.comesFrom === 'forgot-password' && (
        <Alert
          severity="info"
          sx={{
            backgroundColor: theme.palette.info.main,
            color: theme.palette.common.white,
            ['.MuiAlert-icon']: { color: theme.palette.common.white },
          }}
        >
          You will receive an email with instructions about how to reset your
          password in a few minutes.
        </Alert>
      )}
      {query?.comesFrom === 'reset-password' && (
        <Alert
          severity="success"
          sx={{
            backgroundColor: theme.palette.success.main,
            color: theme.palette.common.white,
            ['.MuiAlert-icon']: { color: theme.palette.common.white },
          }}
        >
          Your password was successfully changed.
        </Alert>
      )}
      {isSignup && (
        <>
          <Grid>
            <Typography variant="h5">{title || 'Create an Account'}</Typography>
          </Grid>
          <Grid sx={{ marginTop: '1rem' }}>
            <Typography variant="caption">
              Have an account?{' '}
              <Link
                onClick={() =>
                  isAuthPage ? router.push('/login') : setIsSignup(false)
                }
                sx={{ color: theme.palette.primary.main, cursor: 'pointer' }}
              >
                Log in
              </Link>
            </Typography>
          </Grid>
        </>
      )}
      {!isSignup && (
        <Grid>
          <SocialSignupAndLogin
            isSignup={isSignup}
            referralCode={referralCode}
          />
        </Grid>
      )}
      <Grid sx={{ marginTop: '1.5rem' }}>
        {recaptchaToken && (
          <Form
            onSuccess={onSuccess}
            isSignup={isSignup}
            setIsSignup={setIsSignup}
            defaultValues={defaultSignupValues}
            referralCode={referralCode}
            recaptchaToken={recaptchaToken}
            setHasSignupError={setHasSignupError}
          />
        )}
      </Grid>
      {isSignup && (
        <Grid>
          <SocialSignupAndLogin
            isSignup={isSignup}
            referralCode={referralCode}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default SignupAndLogin;

type SignupOrLogin = 'signup' | 'login';
interface Props {
  state: SignupOrLogin;
  defaultSignupValues?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    password?: string;
    referralCode?: string;
  };
  onSuccess?: () => void;
  referralCode?: string;
  nameParamValue?: string;
  emailParamValue?: string;
  title?: string;
  minHeight?: string;
}
